import type { RefObject } from "react";
import { useEffect } from "react";

/**
 * @description Scrolls the message panel to the bottom when new messages are added
 * @returns Function to scroll the message panel to the bottom manually
 */
export const useChatScroll = ({
  messagePanelRef,
  inputTextAreaRef,
  messages,
}: {
  messagePanelRef: RefObject<HTMLDivElement | null>;
  inputTextAreaRef: RefObject<HTMLTextAreaElement | null>;
  messages: Array<{ id: string }>;
}) => {
  const scrollBehavior = window.matchMedia("(prefers-reduced-motion: reduce)")
    .matches
    ? "auto"
    : "smooth";
  const scrollMessagePanelToBottom = () => {
    messagePanelRef.current?.scrollTo({
      top: messagePanelRef.current.scrollHeight,
      behavior: scrollBehavior,
    });
  };

  // Acceptable (but suboptimal)
  // Change of ref value will not trigger re-render, but if re-render is triggered
  // for any other reason, a change in ref value between renders will cause scrolling as wanted
  // oxlint-disable-next-line react/refs
  const inputTextAreaScrollHeight = inputTextAreaRef.current?.scrollHeight ?? 0;
  const lastMessageIdentity = messages[messages.length - 1]?.id;

  // Scroll to bottom when new messages are added
  useEffect(() => {
    const scrollBehavior = window.matchMedia("(prefers-reduced-motion: reduce)")
      .matches
      ? "auto"
      : "smooth";
    messagePanelRef.current?.scrollTo({
      top: messagePanelRef.current.scrollHeight,
      behavior: scrollBehavior,
    });
    // See above, same use case
    // oxlint-disable-next-line react/refs
  }, [lastMessageIdentity, messagePanelRef, inputTextAreaScrollHeight]);

  return { scrollMessagePanelToBottom };
};
